import {ApolloError, gql} from '@apollo/client';
import {useEffect} from 'react';
import {useLazyQuery$} from '../../../graphqlsupport';
import {useObservable} from '../../../rxsupport';
import {BusinessContactHistoryEntry} from './domain/types';

/** The empty answer, shared so it keeps one identity across renders. */
const NO_ENTRIES: BusinessContactHistoryEntry[] = [];

export const BUSINESS_CONTACT_HISTORY_QUERY = gql`
    query businessContactHistory($contactGuid: UUID!) {
        businessContactHistory(contactGuid: $contactGuid) {
            guid
            changedAt
            source
            operation
            actorName
            actorEmail
            changes {
                field
                before
                after
            }
        }
    }
`;

export type ContactHistory = {
    /** Newest first, the created line last. The backend orders; the panel does not. */
    entries: BusinessContactHistoryEntry[];
    /** True until the first answer lands. */
    loading: boolean;
    error: ApolloError | undefined;
};

/**
 * One contact's history (BHPNPP-3248), read when the panel opens on it. The guid goes in as the
 * hook-level query variable because the first execute() runs those and ignores overrides. The panel
 * is keyed on the guid, so a new contact means a fresh hook and a fresh first run. The effect still
 * passes the guid for the one case left, a re-render that changes the guid in place, which goes
 * through refetch.
 */
export const useContactHistory = (contactGuid: string): ContactHistory => {
    const {execute, data, isRunning$, error} = useLazyQuery$<
        {contactGuid: string},
        {businessContactHistory: BusinessContactHistoryEntry[]}
    >(BUSINESS_CONTACT_HISTORY_QUERY, {contactGuid});

    useEffect(() => {
        execute({contactGuid});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactGuid]);

    const loading = useObservable(isRunning$, true);

    return {entries: data?.businessContactHistory ?? NO_ENTRIES, loading, error};
};
